import { ChevronRight } from "lucide-react";
import { KnowledgeBaseDocument } from "@/generated";
import ImageFallback from "../ImageWithFallback";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";

const MAIN_DATA_NAME = "MAIN_DATA_NAME";

const KBBreadcrumbs = ({
  folder,
  onClick,
}: {
  folder: KnowledgeBaseDocument | undefined;
  onClick: (prefix: string) => void;
}) => {
  const prefix = folder?.prefix
    ? `${folder.prefix}${folder.prefix === "/" ? "" : "/"}${folder.name}`
    : "/";

  const exploded =
    prefix === "/"
      ? [MAIN_DATA_NAME]
      : [MAIN_DATA_NAME, ...prefix.split("/").slice(1)];

  const processed_exploded = exploded.map((e) => ({
    value: e,
    label: e,
  }));

  const handleBreadcrumbClick = (
    entry: { value: string; label: string },
    index: number
  ) => {
    const full_prefix = "/" + exploded.slice(0, index + 1).join("/");

    onClick(full_prefix.replace(`/${MAIN_DATA_NAME}`, ""));
  };

  return (
    <NoxusBreadcrumbs
      entries={processed_exploded}
      onClick={handleBreadcrumbClick}
    />
  );
};

export default KBBreadcrumbs;

export const NoxusBreadcrumbs = ({
  entries,
  onClick,

  onClickRoot,
}: {
  entries: { value: string; label: string }[];
  onClick: (
    { value, label }: { value: string; label: string },
    index: number
  ) => void;

  onClickRoot?: () => void;
}) => {
  function splitArray<T>(arr: T[]): [T[], T[]] {
    if (arr.length <= 3) {
      return [[], arr];
    }
    return [arr.slice(0, -3), arr.slice(-3)];
  }
  const [popoverFolders, breadcumbsFolders] = splitArray(entries);

  if (!entries.length) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {popoverFolders.length > 0 ? (
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <div className="relative w-5 h-5 cursor-pointer">
                <ImageFallback
                  src="https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg"
                  alt="folder"
                  fill
                />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-fit p-3" align="start">
              <div className="flex flex-col gap-3">
                {popoverFolders.map((e, index) => (
                  <div
                    className="flex items-center cursor-pointer gap-2"
                    key={`${e}-${index}`}
                    onClick={() => onClick(e, index)}
                  >
                    <div className="relative w-5 h-5">
                      <ImageFallback
                        src="https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg"
                        alt="folder"
                        fill
                      />
                    </div>
                    <p className="text-sm line-clamp-1 break-all">
                      {e.value === MAIN_DATA_NAME ? "My data" : e.label}
                    </p>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <ChevronRight
            size="0.75rem"
            strokeWidth="1.25px"
            color="#060C11"
            style={{ flexShrink: 0 }}
          />
        </div>
      ) : null}

      {breadcumbsFolders.map((e, i) => (
        <div
          key={`${e}-${i + popoverFolders.length}`}
          className={cn(
            "flex items-center gap-2 min-w-0 max-w-50 cursor-pointer",
            i === breadcumbsFolders.length - 1
              ? "text-primary"
              : "text-muted-foreground"
          )}
          onClick={() => onClick(e, i + popoverFolders.length)}
        >
          <div className="text-sm font-medium line-clamp-1 break-all">
            {e.value === MAIN_DATA_NAME ? "My data" : e.label}
          </div>
          {i < breadcumbsFolders.length - 1 && (
            <ChevronRight
              size="0.75rem"
              strokeWidth="1.25px"
              color="var(--primary)"
              style={{ flexShrink: 0 }}
            />
          )}
        </div>
      ))}
    </div>
  );
};
